import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { Lock, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";

const STORAGE_KEY = "saloni_gate_unlocked_v1";
const PASSCODE = "09032019";
const LEN = PASSCODE.length;

export default function PasscodeGate({ children }) {
  const [unlocked, setUnlocked] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "yes"; } catch { return false; }
  });
  const [value, setValue] = useState("");
  const [status, setStatus] = useState("idle"); // idle | wrong | correct
  const [showBloom, setShowBloom] = useState(false);
  const [gateGone, setGateGone] = useState(unlocked);
  const inputRef = useRef(null);

  useEffect(() => { if (!unlocked) inputRef.current?.focus(); }, [unlocked]);

  // Fire confetti on unlock
  const burst = () => {
    const end = Date.now() + 1200;
    const colors = ["#A7C7E7", "#7B9ACC", "#F7F9FC", "#FFD6E7", "#FFF7B2"];
    (function frame() {
      confetti({ particleCount: 6, angle: 60, spread: 65, startVelocity: 55, origin: { x: 0, y: 0.7 }, colors });
      confetti({ particleCount: 6, angle: 120, spread: 65, startVelocity: 55, origin: { x: 1, y: 0.7 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    confetti({ particleCount: 220, spread: 110, startVelocity: 45, ticks: 260, origin: { x: 0.5, y: 0.55 }, scalar: 1.1, colors });
  };

  const attempt = (v) => {
    if (v.length < LEN) return;
    if (v === PASSCODE) {
      setStatus("correct");
      burst();
      setShowBloom(true);
      try { localStorage.setItem(STORAGE_KEY, "yes"); } catch {}
      // Bridge to AmbientPlayer: it isn't mounted yet, so drop a flag we can read on mount.
      try { sessionStorage.setItem("saloni_should_autoplay", "1"); } catch {}
      try { window.dispatchEvent(new CustomEvent("saloni:unlocked")); } catch {}
      // Sequence: bloom (500ms) -> mark unlocked (frames animate) -> remove from DOM
      setTimeout(() => setUnlocked(true), 700);
      setTimeout(() => setGateGone(true), 2100);
    } else {
      setStatus("wrong");
      setTimeout(() => { setStatus("idle"); setValue(""); inputRef.current?.focus(); }, 700);
    }
  };

  const onChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, LEN);
    setValue(v);
    if (v.length === LEN) attempt(v);
  };

  return (
    <>
      <div style={{ pointerEvents: unlocked ? "auto" : "none" }} aria-hidden={!unlocked}>
        {children}
      </div>
      <AnimatePresence>
        {!gateGone && (
          <motion.div
            key="gate"
            className="fixed inset-0 z-[100] overflow-hidden"
            initial={{ opacity: 1 }}
            animate={{ opacity: unlocked ? 0 : 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="passcode-gate"
          >
            {/* Aurora background */}
            <div className="absolute inset-0 bg-[#050914]" />
            <AuroraBackground unlocked={unlocked} />
            {/* Grain */}
            <div className="absolute inset-0 grain opacity-30" />

            {/* Bloom overlay on correct */}
            <AnimatePresence>
              {showBloom && (
                <motion.div
                  key="bloom"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 22, opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute left-1/2 top-1/2 w-[120px] h-[120px] -translate-x-1/2 -translate-y-1/2 rounded-full"
                  style={{ background: "radial-gradient(circle, #FFF7EA 0%, #A7C7E7 30%, #7B9ACC 60%, transparent 75%)", mixBlendMode: "screen" }}
                />
              )}
            </AnimatePresence>

            {/* Card */}
            <div className="absolute inset-0 flex items-center justify-center px-5">
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{
                  opacity: unlocked ? 0 : 1,
                  y: unlocked ? -40 : 0,
                  scale: unlocked ? 1.08 : 1,
                  filter: unlocked ? "blur(12px)" : "blur(0px)",
                }}
                transition={{ delay: unlocked ? 0 : 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-md"
              >
                <ShakeWrapper shake={status === "wrong"}>
                  <div className="relative rounded-[1.75rem] sm:rounded-[2rem] p-6 sm:p-10 overflow-hidden backdrop-blur-2xl border border-white/12" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)" }}>
                    <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18)" }} />

                    {/* Lock icon w/ pulse */}
                    <div className="relative w-14 h-14 mx-auto rounded-2xl flex items-center justify-center border border-white/20" style={{ background: "linear-gradient(135deg, rgba(167,199,231,0.25), rgba(123,154,204,0.15))" }}>
                      <motion.div
                        animate={{ scale: [1, 1.15, 1], opacity: [0.5, 0.9, 0.5] }}
                        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute inset-0 rounded-2xl border border-[#A7C7E7]/60"
                      />
                      <Lock className="w-6 h-6 text-[#A7C7E7]" />
                    </div>

                    <p className="mt-6 text-center font-body text-[10px] uppercase tracking-[0.4em] text-[#A7C7E7]/80">
                      Private • For Bob's eyes only
                    </p>
                    <h1 className="mt-3 text-center font-display font-bold text-white text-3xl sm:text-4xl leading-tight tracking-tight">
                      A little door,
                      <br />
                      <span className="italic text-[#E4F1FF]">just for you.</span>
                    </h1>
                    <p className="mt-3 text-center font-body text-sm text-white/70 max-w-xs mx-auto">
                      Type the 8 digits only you and Perk would know — the day it all began.
                    </p>

                    {/* Digit slots — tighter on mobile so all 8 fit comfortably */}
                    <div className="relative mt-7 sm:mt-8">
                      <input
                        ref={inputRef}
                        type="tel"
                        inputMode="numeric"
                        autoComplete="off"
                        value={value}
                        onChange={onChange}
                        onKeyDown={(e) => e.key === "Enter" && attempt(value)}
                        maxLength={LEN}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        aria-label="Passcode"
                        data-testid="passcode-input"
                      />
                      <div className="flex justify-between gap-1 sm:gap-2 pointer-events-none">
                        {Array.from({ length: LEN }).map((_, i) => {
                          const filled = i < value.length;
                          const isCurrent = i === value.length && !unlocked;
                          return (
                            <div
                              key={i}
                              className={`flex-1 aspect-[3/4] rounded-lg sm:rounded-xl border flex items-center justify-center transition-all duration-300 ${
                                status === "wrong"
                                  ? "border-rose-400/70 bg-rose-400/10"
                                  : status === "correct"
                                  ? "border-[#A7C7E7] bg-[#A7C7E7]/15"
                                  : filled
                                  ? "border-white/40 bg-white/10"
                                  : isCurrent
                                  ? "border-[#A7C7E7]/60 bg-white/[0.04]"
                                  : "border-white/12 bg-white/[0.02]"
                              }`}
                              style={{ boxShadow: isCurrent ? "0 0 20px rgba(167,199,231,0.35)" : undefined }}
                            >
                              <span className="font-display text-xl sm:text-3xl text-white">
                                {filled ? "•" : ""}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <p className="mt-6 text-center font-hand text-lg text-[#A7C7E7]">
                      {status === "wrong" ? "nope. try again, jaan." : status === "correct" ? "aa gaye hum ✨" : "the door remembers."}
                    </p>

                    {/* Hint pill */}
                    <div className="mt-6 flex items-center justify-center">
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-body text-white/50 border border-white/10 rounded-full px-3 py-1">
                        <Sparkles className="w-3 h-3" /> DDMMYYYY
                      </span>
                    </div>
                  </div>
                </ShakeWrapper>
              </motion.div>
            </div>

            {/* Corner brand */}
            <div className="absolute top-5 left-5 sm:top-6 sm:left-6 font-hand text-base sm:text-lg text-white/70">for Bob • with love</div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function ShakeWrapper({ shake, children }) {
  return (
    <motion.div animate={shake ? { x: [0, -14, 14, -10, 10, -6, 6, 0] } : { x: 0 }} transition={{ duration: 0.55 }}>
      {children}
    </motion.div>
  );
}

function AuroraBackground({ unlocked }) {
  return (
    <>
      <motion.div
        aria-hidden
        initial={{ opacity: 0.6, scale: 1 }}
        animate={{ opacity: unlocked ? 1.3 : 0.6, scale: unlocked ? 1.35 : 1 }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(60% 60% at 20% 20%, rgba(167,199,231,0.45) 0%, transparent 60%), radial-gradient(50% 50% at 80% 30%, rgba(123,154,204,0.45) 0%, transparent 60%), radial-gradient(70% 55% at 60% 90%, rgba(255,214,231,0.30) 0%, transparent 60%), radial-gradient(45% 45% at 10% 80%, rgba(255,247,178,0.20) 0%, transparent 60%)",
          filter: "blur(30px)",
        }}
      />
      {/* Slowly drifting blobs */}
      <FloatBlob className="left-[-6%] top-[10%]" color="rgba(167,199,231,0.35)" size={520} duration={18} />
      <FloatBlob className="right-[-8%] top-[30%]" color="rgba(123,154,204,0.30)" size={480} duration={22} delay={2} />
      <FloatBlob className="left-[20%] bottom-[-10%]" color="rgba(255,214,231,0.22)" size={560} duration={26} delay={5} />
    </>
  );
}

function FloatBlob({ className = "", color, size = 480, duration = 20, delay = 0 }) {
  return (
    <motion.div
      aria-hidden
      className={`absolute rounded-full pointer-events-none ${className}`}
      style={{ width: size, height: size, background: `radial-gradient(circle, ${color} 0%, transparent 70%)`, filter: "blur(20px)" }}
      animate={{ x: [0, 20, -14, 0], y: [0, -18, 12, 0], scale: [1, 1.05, 0.98, 1] }}
      transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
    />
  );
}
